<script setup>
import { ref, reactive, onMounted } from 'vue';
import Draggable from 'vuedraggable';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  KanbanColumnsAPIClient,
  KanbanCardsAPIClient,
} from 'dashboard/api/kanban';

const store = useStore();
const agents = useMapGetter('agents/getAgents');

const columns = ref([]);
const loading = ref(true);

const showCardModal = ref(false);
const editingCard = ref(null);
const targetColumnId = ref(null);
const cardForm = reactive({
  title: '',
  contact_name: '',
  contact_phone: '',
  value: '',
  assignee_id: '',
  description: '',
});

const BRAND = '#6366F1';

const formatCurrency = value => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  if (Number.isNaN(number)) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(number);
};

const loadBoard = async () => {
  loading.value = true;
  try {
    const { data } = await KanbanColumnsAPIClient.get();
    columns.value = (data || []).map(column => ({
      ...column,
      cards: column.cards || [],
    }));
  } catch (error) {
    useAlert('Não foi possível carregar o quadro.');
  } finally {
    loading.value = false;
  }
};

const addColumn = async () => {
  // eslint-disable-next-line no-alert
  const name = window.prompt('Nome da nova etapa:');
  if (!name) return;
  try {
    const { data } = await KanbanColumnsAPIClient.create({
      kanban_column: { name, position: columns.value.length, color: BRAND },
    });
    columns.value.push({ ...data, cards: [] });
  } catch (error) {
    useAlert('Não foi possível criar a etapa.');
  }
};

const renameColumn = async column => {
  // eslint-disable-next-line no-alert
  const name = window.prompt('Renomear etapa:', column.name);
  if (!name || name === column.name) return;
  try {
    await KanbanColumnsAPIClient.update(column.id, {
      kanban_column: { name },
    });
    column.name = name;
  } catch (error) {
    useAlert('Não foi possível renomear a etapa.');
  }
};

const deleteColumn = async column => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Excluir a etapa "${column.name}" e seus cards?`)) return;
  try {
    await KanbanColumnsAPIClient.delete(column.id);
    columns.value = columns.value.filter(item => item.id !== column.id);
  } catch (error) {
    useAlert('Não foi possível excluir a etapa.');
  }
};

const reindexColumn = async column => {
  const updates = [];
  column.cards.forEach((card, index) => {
    if (card.position !== index || card.kanban_column_id !== column.id) {
      card.position = index;
      card.kanban_column_id = column.id;
      updates.push(
        KanbanCardsAPIClient.update(card.id, {
          kanban_card: { position: index, kanban_column_id: column.id },
        })
      );
    }
  });
  try {
    await Promise.all(updates);
  } catch (error) {
    useAlert('Não foi possível mover o card.');
  }
};

const onCardChange = (event, column) => {
  if (event.added || event.moved || event.removed) {
    reindexColumn(column);
  }
};

const resetForm = () => {
  cardForm.title = '';
  cardForm.contact_name = '';
  cardForm.contact_phone = '';
  cardForm.value = '';
  cardForm.assignee_id = '';
  cardForm.description = '';
};

const openNewCard = column => {
  editingCard.value = null;
  targetColumnId.value = column.id;
  resetForm();
  showCardModal.value = true;
};

const openEditCard = card => {
  editingCard.value = card;
  targetColumnId.value = card.kanban_column_id;
  cardForm.title = card.title || '';
  cardForm.contact_name = card.contact_name || '';
  cardForm.contact_phone = card.contact_phone || '';
  cardForm.value = card.value || '';
  cardForm.assignee_id = card.assignee_id || '';
  cardForm.description = card.description || '';
  showCardModal.value = true;
};

const closeModal = () => {
  showCardModal.value = false;
};

const saveCard = async () => {
  if (!cardForm.title.trim()) {
    useAlert('Informe um título para o card.');
    return;
  }
  const payload = {
    kanban_card: {
      title: cardForm.title,
      contact_name: cardForm.contact_name,
      contact_phone: cardForm.contact_phone,
      value: cardForm.value === '' ? null : cardForm.value,
      assignee_id: cardForm.assignee_id === '' ? null : cardForm.assignee_id,
      description: cardForm.description,
    },
  };
  try {
    if (editingCard.value) {
      await KanbanCardsAPIClient.update(editingCard.value.id, payload);
    } else {
      const column = columns.value.find(c => c.id === targetColumnId.value);
      payload.kanban_card.kanban_column_id = targetColumnId.value;
      payload.kanban_card.position = column ? column.cards.length : 0;
      await KanbanCardsAPIClient.create(payload);
    }
    closeModal();
    await loadBoard();
  } catch (error) {
    useAlert('Não foi possível salvar o card.');
  }
};

const deleteCard = async card => {
  // eslint-disable-next-line no-alert
  if (!window.confirm('Excluir este card?')) return;
  try {
    await KanbanCardsAPIClient.delete(card.id);
    await loadBoard();
  } catch (error) {
    useAlert('Não foi possível excluir o card.');
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  loadBoard();
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between flex-shrink-0 px-6 py-4 border-b border-n-weak"
    >
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">CRM KALUX</h1>
        <p class="text-sm text-n-slate-11">Pipeline de leads e clientes</p>
      </div>
      <button
        class="px-3 py-2 text-sm font-medium text-white rounded-lg"
        :style="{ backgroundColor: BRAND }"
        @click="addColumn"
      >
        + Nova etapa
      </button>
    </header>

    <div v-if="loading" class="flex items-center justify-center flex-1">
      <span class="text-sm text-n-slate-11">Carregando quadro…</span>
    </div>

    <div v-else class="flex flex-1 gap-4 px-6 py-4 overflow-x-auto">
      <div
        v-for="column in columns"
        :key="column.id"
        class="flex flex-col flex-shrink-0 w-72 rounded-xl bg-n-solid-1 border border-n-weak max-h-full"
      >
        <div
          class="flex items-center justify-between px-3 py-2 border-t-4 rounded-t-xl"
          :style="{ borderColor: column.color || BRAND }"
        >
          <span class="text-sm font-semibold text-n-slate-12 truncate">
            {{ column.name }}
            <span class="ml-1 text-n-slate-10">({{ column.cards.length }})</span>
          </span>
          <div class="flex items-center gap-1">
            <button
              class="text-xs text-n-slate-10 hover:text-n-slate-12"
              title="Renomear"
              @click="renameColumn(column)"
            >
              ✎
            </button>
            <button
              class="text-xs text-n-slate-10 hover:text-n-ruby-11"
              title="Excluir etapa"
              @click="deleteColumn(column)"
            >
              ✕
            </button>
          </div>
        </div>

        <Draggable
          :list="column.cards"
          group="kanban"
          item-key="id"
          class="flex-1 px-2 py-2 space-y-2 overflow-y-auto min-h-[60px]"
          @change="event => onCardChange(event, column)"
        >
          <template #item="{ element }">
            <div
              class="p-3 bg-n-background rounded-lg border border-n-weak cursor-grab hover:border-n-slate-7"
              @click="openEditCard(element)"
            >
              <p class="text-sm font-medium text-n-slate-12">
                {{ element.title }}
              </p>
              <p
                v-if="element.contact_name"
                class="mt-1 text-xs text-n-slate-11"
              >
                {{ element.contact_name }}
              </p>
              <p
                v-if="element.contact_phone"
                class="text-xs text-n-slate-10"
              >
                {{ element.contact_phone }}
              </p>
              <div class="flex items-center justify-between mt-2">
                <span
                  v-if="element.value"
                  class="text-xs font-semibold"
                  :style="{ color: BRAND }"
                >
                  {{ formatCurrency(element.value) }}
                </span>
                <span
                  v-if="element.assignee_name"
                  class="text-xs text-n-slate-10 truncate"
                >
                  {{ element.assignee_name }}
                </span>
              </div>
            </div>
          </template>
        </Draggable>

        <button
          class="px-3 py-2 text-xs text-left text-n-slate-10 hover:text-n-slate-12"
          @click="openNewCard(column)"
        >
          + Adicionar card
        </button>
      </div>

      <div
        v-if="!columns.length"
        class="flex items-center justify-center flex-1 text-sm text-n-slate-11"
      >
        Nenhuma etapa ainda. Clique em "+ Nova etapa" para começar.
      </div>
    </div>

    <!-- Card modal -->
    <div
      v-if="showCardModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="closeModal"
    >
      <div class="w-full max-w-md p-5 bg-n-solid-1 rounded-xl shadow-lg">
        <h2 class="mb-4 text-base font-semibold text-n-slate-12">
          {{ editingCard ? 'Editar card' : 'Novo card' }}
        </h2>
        <div class="space-y-3">
          <input
            v-model="cardForm.title"
            type="text"
            placeholder="Título *"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-background border border-n-weak text-n-slate-12"
          />
          <input
            v-model="cardForm.contact_name"
            type="text"
            placeholder="Nome do cliente"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-background border border-n-weak text-n-slate-12"
          />
          <input
            v-model="cardForm.contact_phone"
            type="text"
            placeholder="Telefone"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-background border border-n-weak text-n-slate-12"
          />
          <input
            v-model="cardForm.value"
            type="number"
            step="0.01"
            placeholder="Valor (R$)"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-background border border-n-weak text-n-slate-12"
          />
          <select
            v-model="cardForm.assignee_id"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-background border border-n-weak text-n-slate-12"
          >
            <option value="">Sem responsável</option>
            <option
              v-for="agent in agents"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>
          <textarea
            v-model="cardForm.description"
            rows="3"
            placeholder="Observações"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-background border border-n-weak text-n-slate-12"
          />
        </div>
        <div class="flex items-center justify-between mt-5">
          <button
            v-if="editingCard"
            class="text-sm text-n-ruby-11"
            @click="deleteCard(editingCard)"
          >
            Excluir
          </button>
          <div class="flex gap-2 ml-auto">
            <button
              class="px-3 py-2 text-sm rounded-lg text-n-slate-11 hover:text-n-slate-12"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white rounded-lg"
              :style="{ backgroundColor: BRAND }"
              @click="saveCard"
            >
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
